import { env } from 'env';
import { EventStatus, IEvent } from '@interfaces/event';
import { IWorldSettings } from '@interfaces/worlds';

const {
  REACT_APP_ALWAYS_ON_EVENT_TAGS,
  REACT_APP_SCHEDULED_EVENT_TAGS,
  REACT_APP_MANDATORY_EVENT_TAG = '',
} = env;

const ALWAYS_ON_TAGS = REACT_APP_ALWAYS_ON_EVENT_TAGS?.split(',').map((x) =>
  x.toLowerCase().trim(),
);
const SCHEDULED_TAGS = REACT_APP_SCHEDULED_EVENT_TAGS?.split(',').map((x) =>
  x.toLowerCase().trim(),
);

export const groupEvents = (events: IEvent[]) => {
  if (!events.length || (!ALWAYS_ON_TAGS?.length && !SCHEDULED_TAGS?.length)) {
    return events;
  }

  const alwaysOnTagsSet = new Set(
    ALWAYS_ON_TAGS?.map((tag) => tag.toLowerCase()) || [],
  );
  const scheduledTagsSet = new Set(
    SCHEDULED_TAGS?.map((tag) => tag.toLowerCase()) || [],
  );

  const alwaysOnIds = new Set<string>();
  const scheduledIds = new Set<string>();

  sortEventsByTime(events);

  for (const event of events) {
    if (!event?.eventTags?.length) {
      alwaysOnIds.add(event.eventId);
      event.isAlwaysOn = true;
      continue;
    }

    let grouped = false;

    // Check for always-on events
    if (
      alwaysOnTagsSet.size > 0 &&
      !alwaysOnIds.has(event.eventId) &&
      hasMatchingTags(event.eventTags, alwaysOnTagsSet)
    ) {
      alwaysOnIds.add(event.eventId);
      grouped = true;
      event.isAlwaysOn = true;
    }

    // Check for scheduled events
    if (
      scheduledTagsSet.size > 0 &&
      !scheduledIds.has(event.eventId) &&
      hasMatchingTags(event.eventTags, scheduledTagsSet)
    ) {
      scheduledIds.add(event.eventId);
      event.isScheduled = true;
      grouped = true;
    }

    // If not grouped, add to always-on events
    if (!grouped) {
      event.isAlwaysOn = true;
      alwaysOnIds.add(event.eventId);
    }
  }

  return events;
};

export const convertUnixTimeToMilliSeconds = (timestamp: number) => {
  const threshold = 1e12; // ~2001 in milliseconds

  return timestamp > threshold ? timestamp : timestamp * 1000;
};

export const ellipsizeText = (text: string, maxLength: number) => {
  if (typeof text !== 'string') {
    return '';
  }

  if (text.length <= maxLength) {
    return text;
  }

  const words = text.split(' ');
  let result = '';

  for (let i = 0; i < words.length; i++) {
    const nextWord = result ? `${result} ${words[i]}` : words[i];

    if (nextWord.length > maxLength - 1) {
      break; // -1 to reserve room for "…"
    }

    result = nextWord;
  }

  // Fallback: if no full word fits, hard truncate and avoid cutting trailing space
  if (!result) {
    result = text.slice(0, maxLength - 1).replace(/\s+$/, '');
  }

  return `${result}…`;
};

export const isScheduledEvent = (event: IEvent) => {
  const scheduledTagsSet = new Set(
    SCHEDULED_TAGS?.map((tag) => tag.toLowerCase()) || [],
  );
  if (event && event.eventTags) {
    return hasMatchingTags(event.eventTags, scheduledTagsSet);
  }
  return false;
};

const hasMatchingTags = (eventTags: string[], tagSet: Set<string>): boolean => {
  return eventTags.some((tag) => tagSet.has(tag.toLowerCase()));
};

const sortEventsByTime = (events: IEvent[]) => {
  return events.sort((a, b) => {
    const timeA =
      a.inviteStartTime && a.inviteStartTime > 0
        ? a.inviteStartTime
        : a.startTime;
    const timeB =
      b.inviteStartTime && b.inviteStartTime > 0
        ? b.inviteStartTime
        : b.startTime;

    return timeA - timeB;
  });
};

const isInTimeRange = (
  startTime: number,
  minutesBeforeStar: number,
  minutesAfterStart: number,
) => {
  const now = Date.now();
  const fiveMinutesFromNow = now + minutesBeforeStar * 60 * 1000;
  const fifteenMinutesAgo = now - minutesAfterStart * 60 * 1000;

  return startTime <= fiveMinutesFromNow && startTime >= fifteenMinutesAgo;
};

export const processMandatoryEvents = (
  events: IEvent[],
  appSettings: IWorldSettings | null,
) => {
  if (!REACT_APP_MANDATORY_EVENT_TAG) {
    return events;
  }

  const mandatoryEvent = events.find((event) => {
    if (
      event.eventTags?.includes(REACT_APP_MANDATORY_EVENT_TAG) &&
      event.status === EventStatus.ACTIVE
    ) {
      const minutesBeforeStart =
        appSettings?.mandatoryEvent?.disableBeforeStartMin || 5;
      const minutesAfterStart =
        appSettings?.mandatoryEvent?.disableAfterStartMin || 15;

      return isInTimeRange(
        event.startTime * 1000,
        minutesBeforeStart,
        minutesAfterStart,
      );
    }

    return false;
  });

  if (mandatoryEvent) {
    return events.map((event) => {
      if (event.eventId === mandatoryEvent.eventId) {
        return event;
      }

      return { ...event, isDisabled: true };
    });
  }

  return events;
};
